import { useState } from 'react';
import { OUT_OF_SERVICE_KINDS, type OutOfServiceKind } from '../types/database';

// "PC uit de line-up halen" — the operator takes one machine out of rotation.
//
// A CATEGORY is required, the note is not. That asymmetry is the whole design:
// the note is what the next operator reads, but a note alone cannot be counted,
// and "waarom staat die PC uit?" at 11:00 on a Saturday is a question somebody
// always has to answer from memory otherwise (Dirk 2026-09-16).
//
// The modal deliberately does NOT offer to shut the PC down or pause its sync.
// Out of service means "stuur hier geen bezoeker heen"; the machine keeps
// heartbeating and keeps syncing content so it is ready the moment it returns.

interface Props {
  pcName: string;
  /** Somebody is playing right now: taking the PC out mid-run is blocked. */
  runActive?: boolean;
  onConfirm: (kind: OutOfServiceKind, note: string) => void;
  onClose: () => void;
}

export default function OutOfServiceModal({ pcName, runActive, onConfirm, onClose }: Props) {
  const [kind, setKind] = useState<OutOfServiceKind | null>(null);
  const [note, setNote] = useState('');

  return (
    <div className="oos-overlay" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="oos-modal" onClick={(e) => e.stopPropagation()}>
        <header className="oos-header">
          <h2>{pcName} uit de line-up halen</h2>
          <button className="oos-close" onClick={onClose} aria-label="Sluiten">✕</button>
        </header>

        <p className="oos-intro">
          Deze PC krijgt geen bezoekers meer en telt niet meer mee in de wachttijden.
          Hij blijft wel aan staan en synchroniseert gewoon door.
        </p>

        {runActive ? (
          <div className="oos-blocked">
            Er speelt nu iemand op deze PC. Stop die run eerst — anders staat de
            bezoeker halverwege zonder begeleiding.
          </div>
        ) : null}

        <div className="oos-label">WAAROM?</div>
        <div className="oos-kinds">
          {OUT_OF_SERVICE_KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              className={`oos-kind${kind === k.key ? ' oos-kind--on' : ''}`}
              onClick={() => setKind(k.key)}
            >
              <span className="oos-kind-label">{k.label}</span>
              <span className="oos-kind-hint">{k.hint}</span>
            </button>
          ))}
        </div>

        <div className="oos-label">NOTITIE VOOR DE VOLGENDE OPERATOR</div>
        <textarea
          className="oos-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={280}
          rows={3}
          placeholder="Bijvoorbeeld: wacht op nieuwe kabel, besteld bij Dirk"
        />

        <footer className="oos-footer">
          <button className="oos-secondary" onClick={onClose}>Annuleren</button>
          <button
            className="oos-primary"
            disabled={!kind || !!runActive}
            title={runActive ? 'Stop eerst de lopende run' : !kind ? 'Kies eerst een reden' : undefined}
            onClick={() => kind && onConfirm(kind, note)}
          >
            Uit de line-up halen
          </button>
        </footer>
      </div>
    </div>
  );
}
